import { Link } from "react-router-dom";
import { CheckCircle2, X, ArrowRight, HelpCircle } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { SEOHead } from "@/components/SEOHead";

const PACKAGES = [
  {
    id: "starter",
    name: "Starter",
    monthly: "39,99",
    setup: "199",
    desc: "Für Selbstständige und kleine Unternehmen die endlich professionell online sichtbar sein wollen.",
    color: "border-white/8",
    badge: null,
    highlight: false,
  },
  {
    id: "business",
    name: "Business",
    monthly: "59,99",
    setup: "249",
    desc: "Für wachsende Unternehmen mit Online-Buchungsbedarf und höherer Suchmaschinen-Sichtbarkeit.",
    color: "border-cyan-400/40",
    badge: "Meistgewählt",
    highlight: true,
  },
  {
    id: "premium",
    name: "Premium",
    monthly: "99",
    setup: "499",
    desc: "Für Unternehmen die aktiv wachsen wollen — mit begleitendem SEO und persönlichem Strategy-Call.",
    color: "border-white/8",
    badge: null,
    highlight: false,
  },
];

const FEATURE_ROWS = [
  {
    category: "Website",
    items: [
      { label: "Unterseiten", starter: "1 Seite", business: "Bis zu 5", premium: "Unbegrenzt" },
      { label: "Professionelles Design", starter: true, business: true, premium: true },
      { label: "Mobile-optimiert", starter: true, business: true, premium: true },
      { label: "Individuelle Domain-Anbindung", starter: true, business: true, premium: true },
      { label: "SSL-Zertifikat (HTTPS)", starter: true, business: true, premium: true },
      { label: "DSGVO-konform", starter: true, business: true, premium: true },
    ],
  },
  {
    category: "Funktionen",
    items: [
      { label: "Kontaktformular", starter: true, business: true, premium: true },
      { label: "Online-Buchungssystem", starter: false, business: true, premium: true },
      { label: "Mehrsprachigkeit", starter: false, business: false, premium: true },
      { label: "Blog / Neuigkeiten", starter: false, business: true, premium: true },
      { label: "Galerie / Portfolio", starter: false, business: true, premium: true },
    ],
  },
  {
    category: "Marketing & SEO",
    items: [
      { label: "SEO-Grundoptimierung", starter: true, business: true, premium: true },
      { label: "Volles On-Page SEO", starter: false, business: true, premium: true },
      { label: "Aktives monatliches SEO", starter: false, business: false, premium: true },
      { label: "Google My Business Setup", starter: false, business: true, premium: true },
      { label: "Analytics-Integration", starter: false, business: true, premium: true },
      { label: "Monatliche Performance-Berichte", starter: false, business: true, premium: true },
      { label: "Konkurrenz-Analyse", starter: false, business: false, premium: true },
    ],
  },
  {
    category: "Support & Betreuung",
    items: [
      { label: "Kostenloser Support (1. Monat)", starter: true, business: true, premium: true },
      { label: "Priority-Support", starter: false, business: true, premium: true },
      { label: "Content-Updates inklusive", starter: false, business: false, premium: true },
      { label: "Monatlicher Strategy-Call", starter: false, business: false, premium: true },
      { label: "Dedizierter Ansprechpartner", starter: false, business: false, premium: true },
    ],
  },
];

const FAQS = [
  {
    q: "Wie schnell ist meine Website live?",
    a: "Nach Ihrer Freigabe der Demo-Website benötigen wir in der Regel 24–48 Stunden bis zur finalen Live-Schaltung. Die Demo selbst erhalten Sie bereits innerhalb von 24 Stunden nach unserem Erstgespräch.",
  },
  {
    q: "Was ist der Unterschied zur einmaligen Einrichtungsgebühr?",
    a: "Die einmalige Gebühr deckt die initiale Erstellung Ihrer Website ab: Design, Einrichtung, Texte, Bilder und technische Konfiguration. Die monatliche Rate beinhaltet Hosting, Pflege, Updates und Support.",
  },
  {
    q: "Kann ich das Paket wechseln?",
    a: "Ja, Sie können jederzeit auf ein höheres Paket upgraden. Ein Downgrade ist zum Ende des laufenden Monats möglich.",
  },
  {
    q: "Wie lange ist die Mindestlaufzeit?",
    a: "Es gibt keine Mindestlaufzeit. Alle Pakete sind monatlich kündbar — mit einer Kündigungsfrist von 30 Tagen zum Monatsende.",
  },
  {
    q: "Bekomme ich meine Website-Daten wenn ich kündige?",
    a: "Selbstverständlich. Beim Kündigen erhalten Sie alle Ihre Website-Inhalte und können diese zu einem anderen Anbieter mitnehmen.",
  },
  {
    q: "Ist das Online-Buchungssystem für alle Branchen geeignet?",
    a: "Das Buchungssystem eignet sich für alle terminfähigen Branchen: Friseur, Physiotherapie, Kosmetik, Handwerk, Beratung und viele mehr.",
  },
];

function FeatureValue({ value }) {
  if (value === true) return <CheckCircle2 size={18} className="text-green-400 mx-auto" />;
  if (value === false) return <X size={16} className="text-white/20 mx-auto" />;
  return <span className="text-sm text-white/70 font-medium">{value}</span>;
}

function FAQItem({ q, a }) {
  return (
    <details className="group border border-white/5 rounded-xl overflow-hidden">
      <summary className="flex items-center justify-between px-6 py-5 cursor-pointer hover:bg-white/3 transition-colors list-none">
        <span className="font-medium text-white text-sm pr-4">{q}</span>
        <ChevronIcon />
      </summary>
      <div className="px-6 pb-5 text-sm text-white/55 leading-relaxed border-t border-white/5 pt-4">
        {a}
      </div>
    </details>
  );
}

function ChevronIcon() {
  return (
    <svg className="w-5 h-5 text-white/30 flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export default function PreisePage() {
  return (
    <PublicLayout>
      <SEOHead
        title="Leistungen & Pakete"
        description="Starter, Business oder Premium — transparente Website-Pakete auf Anfrage. Vergleichen Sie Funktionen und finden Sie das richtige Paket für Ihr Unternehmen."
        canonical="/leistungen"
      />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-4">Unsere Leistungen</p>
          <h1
            className="font-black text-5xl sm:text-6xl text-white mb-5 leading-tight"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Drei Pakete. Ein Versprechen.
          </h1>
          <p className="text-lg text-white/50 leading-relaxed">
            Klare Pakete, individuelle Angebote. Sag uns was du brauchst — wir machen dir ein faires Angebot, ganz ohne Standardpreise und Fallstricke.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`pricing-card-${pkg.id}`}
              className={`relative bg-[#0A1220] border-2 ${pkg.color} rounded-2xl p-8 flex flex-col ${
                pkg.highlight ? "shadow-xl shadow-cyan-400/10 scale-[1.02]" : ""
              }`}
            >
              {pkg.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-cyan-400 text-slate-900 text-xs font-bold rounded-full shadow-lg">
                  {pkg.badge}
                </div>
              )}
              <div className="mb-6">
                <p className="text-white font-semibold text-lg mb-2">{pkg.name}</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-black text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Auf Anfrage
                  </span>
                </div>
                <p className="text-xs text-white/30">Individuelles Angebot in 24 Std.</p>
              </div>
              <p className="text-sm text-white/50 leading-relaxed mb-6 flex-1">{pkg.desc}</p>
              <Link
                to={`/kontakt?paket=${pkg.id}#demo-form`}
                className={`w-full py-3.5 rounded-xl font-semibold text-sm text-center transition-all ${
                  pkg.highlight
                    ? "bg-cyan-400 text-slate-900 hover:bg-cyan-300 shadow-lg shadow-cyan-400/20"
                    : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                }`}
              >
                {pkg.name} anfragen
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          <h2
            className="font-black text-3xl text-white text-center mb-10"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Funktionen im Detail
          </h2>

          <div className="bg-[#0A1220] border border-white/5 rounded-2xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-4 border-b border-white/5">
              <div className="p-5 text-xs text-white/30 font-semibold uppercase tracking-widest">Funktion</div>
              {PACKAGES.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`p-5 text-center ${pkg.highlight ? "bg-cyan-400/5" : ""}`}
                >
                  <p className={`font-bold text-sm ${pkg.highlight ? "text-cyan-400" : "text-white/70"}`}>
                    {pkg.name}
                  </p>
                </div>
              ))}
            </div>

            {/* Rows */}
            {FEATURE_ROWS.map((group, gi) => (
              <div key={group.category}>
                <div className="px-5 py-3 bg-white/2 border-t border-white/5">
                  <span className="text-xs font-bold uppercase tracking-widest text-white/30">{group.category}</span>
                </div>
                {group.items.map((row, ri) => (
                  <div
                    key={row.label}
                    className={`grid grid-cols-4 border-t border-white/3 hover:bg-white/2 transition-colors ${ri % 2 === 0 ? "" : "bg-white/1"}`}
                  >
                    <div className="p-4 text-sm text-white/60">{row.label}</div>
                    {["starter", "business", "premium"].map((key) => (
                      <div key={key} className={`p-4 flex items-center justify-center ${PACKAGES.find(p => p.id === key)?.highlight ? "bg-cyan-400/3" : ""}`}>
                        <FeatureValue value={row[key]} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 pb-24 bg-[#070d1a] py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-3">FAQ</p>
            <h2
              className="font-black text-4xl text-white"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Häufige Fragen
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2
            className="font-black text-4xl text-white mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Nicht sicher welches Paket?
          </h2>
          <p className="text-white/50 mb-8">
            In einem kostenlosen Gespräch finden wir gemeinsam das Richtige für Ihr Unternehmen.
          </p>
          <Link
            to="/kontakt#demo-form"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-xl shadow-cyan-400/20 text-base"
          >
            Kostenlos beraten lassen <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
